import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import {
    PACOCA_API_URL,
    VERSONA_API_URL,
    REFLEXO_API_URL,
} from '../global-components/src/utils/vars';
import i18n from '../global-components/src/i18n';
import { getDeviceId } from '../utils/device-id';
import { mutationRateGuard } from '../security/mutation-rate-guard';

const FIRST_PARTY_API_URLS = [
    PACOCA_API_URL,
    VERSONA_API_URL,
    REFLEXO_API_URL,
].map(url => url.replace(/\/+$/, ''));

const PACOCA_API_BASE = PACOCA_API_URL.replace(/\/+$/, '');
const PACOCA_CLIENT = 'frontend';

type SessionRefreshHandler = (failedToken: string) => Promise<string | null>;

let sessionRefreshHandler: SessionRefreshHandler | null = null;
let refreshInFlight: Promise<string | null> | null = null;

export const setSessionRefreshHandler = (handler: SessionRefreshHandler | null) => {
    sessionRefreshHandler = handler;
};

const isFirstPartyApiRequest = (url?: string, baseURL?: string): boolean => {
    const candidates = [url, baseURL].filter((value): value is string => !!value);

    return candidates.some(candidate => {
        const normalized = candidate.replace(/\/+$/, '');
        return FIRST_PARTY_API_URLS.some(apiBase => (
            normalized === apiBase || normalized.startsWith(`${apiBase}/`)
        ));
    });
};

const isPacocaApiRequest = (url?: string, baseURL?: string): boolean => {
    const candidates = [url, baseURL].filter((value): value is string => !!value);

    return candidates.some(candidate => {
        const normalized = candidate.replace(/\/+$/, '');
        return normalized === PACOCA_API_BASE || normalized.startsWith(`${PACOCA_API_BASE}/`);
    });
};

const isRefreshRequest = (url?: string): boolean => {
    return !!url && /(?:^|\/)refresh(?:\?|$)/.test(url);
};

const isMutation = (method?: string): boolean => {
    return ['post', 'put', 'patch', 'delete'].includes(method?.toLowerCase() ?? 'get');
};

const tokenFromConfig = (config?: AxiosRequestConfig): string | null => {
    const authorization = config?.headers?.Authorization ?? config?.headers?.authorization;
    if (typeof authorization !== 'string') return null;

    const match = authorization.match(/^Bearer\s+(.+)$/i);
    return match?.[1]?.trim() || null;
};

const refreshSession = async (token: string): Promise<string | null> => {
    if (!sessionRefreshHandler) return null;

    if (!refreshInFlight) {
        refreshInFlight = Promise.resolve(sessionRefreshHandler(token))
            .catch(() => null)
            .finally(() => {
                refreshInFlight = null;
            });
    }

    return refreshInFlight;
};

const retryAfterRefresh = async (
    error: AxiosError,
    retry: (config: AxiosRequestConfig) => Promise<unknown>,
) => {
    const config = error.config as (AxiosRequestConfig & { __pacocaSessionRetry?: boolean }) | undefined;
    const token = tokenFromConfig(config);

    if (
        error.response?.status !== 401 ||
        !config ||
        !token ||
        config.__pacocaSessionRetry ||
        !isPacocaApiRequest(config.url, config.baseURL) ||
        isRefreshRequest(config.url)
    ) {
        throw error;
    }

    config.__pacocaSessionRetry = true;
    const refreshedToken = await refreshSession(token);
    if (!refreshedToken) throw error;

    config.headers = {
        ...(config.headers ?? {}),
        Authorization: `Bearer ${refreshedToken}`,
    };

    return retry(config);
};

// Interceptor global: garante que chamadas diretas à API do Paçoca enviem os
// headers compartilhados sem vazar X-Device-Id para uploads e APIs externas.
axios.interceptors.request.use((config) => {
    config.headers['Accept-Language'] = i18n.language;

    if (
        isFirstPartyApiRequest(config.url, config.baseURL) &&
        isMutation(config.method) &&
        !isRefreshRequest(config.url)
    ) {
        mutationRateGuard.check();
    }

    if (isFirstPartyApiRequest(config.url, config.baseURL)) {
        const deviceId = getDeviceId();
        if (deviceId) config.headers['X-Device-Id'] = deviceId;
    }

    if (isPacocaApiRequest(config.url, config.baseURL)) {
        config.headers['X-Pacoca-Client'] = PACOCA_CLIENT;
    }

    return config;
});

axios.interceptors.response.use(
    response => response,
    error => retryAfterRefresh(error, config => axios.request(config)),
);

export const api = (token?: string) => {
    const deviceId = getDeviceId();
    const instance = axios.create({
        baseURL: PACOCA_API_URL,
        headers: {
            'Accept-Language': i18n.language,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Pacoca-Client': PACOCA_CLIENT,
            ...(deviceId ? { 'X-Device-Id': deviceId } : {}),
        },
    });

    instance.interceptors.response.use(
        response => response,
        error => retryAfterRefresh(error, config => instance.request(config)),
    );

    return instance;
};
